import { isTutorialMode } from './mode';
import { TutorialIsolationError } from './errors';

/**
 * Armazenamento do modo demonstracao (spec 6.1-2).
 *
 * O iframe do tutorial e' da MESMA origem da janela real, entao compartilha o
 * `localStorage`/`sessionStorage` dela. Sem isolamento, o primeiro `set` de um
 * store persistido (`opruscrm-auth`, `opruscrm-tenant`, ...) dentro do iframe
 * sobrescreveria a sessao de verdade da pessoa.
 */

/** `Storage` que vive so na memoria desta janela. */
export function createMemoryStorage(): Storage {
  const entries = new Map<string, string>();
  return {
    get length() {
      return entries.size;
    },
    clear: () => entries.clear(),
    getItem: (key) => entries.get(key) ?? null,
    key: (index) => [...entries.keys()][index] ?? null,
    removeItem: (key) => {
      entries.delete(key);
    },
    setItem: (key, value) => {
      entries.set(key, String(value));
    },
  };
}

/** Storage unico do modo demonstracao — os stores persistidos escrevem aqui. */
export const tutorialStorage: Storage = createMemoryStorage();

/**
 * Storage que os stores persistidos devem usar
 * (`createJSONStorage(persistStorage)`): o real fora do tutorial, a memoria
 * dentro dele. Resolvido na hora da chamada, nao no import.
 */
export function persistStorage(): Storage {
  return isTutorialMode() ? tutorialStorage : window.localStorage;
}

type StorageHost = { localStorage: Storage; sessionStorage: Storage };

function replaceStorage(host: StorageHost, name: keyof StorageHost, storage: Storage): void {
  try {
    Object.defineProperty(host, name, { configurable: true, enumerable: true, get: () => storage });
  } catch (error) {
    throw new TutorialIsolationError(`nao foi possivel isolar window.${name}`, { cause: error });
  }
  if (host[name] !== storage) {
    throw new TutorialIsolationError(`window.${name} continua apontando para o armazenamento real`);
  }
}

/**
 * Troca `localStorage` e `sessionStorage` da janela por armazenamento em
 * memoria. Cobre tudo que le o global direto — o detector de idioma do i18next
 * (`language-storage`), stores persistidos fora da lista da spec
 * (`ui-storage`, ...) e `getToken()`. Se o navegador nao
 * deixar, FALHA: abrir o tutorial com risco de mexer na sessao real nao e'
 * opcao.
 */
export function isolateBrowserStorage(host: StorageHost = window): void {
  replaceStorage(host, 'localStorage', tutorialStorage);
  replaceStorage(host, 'sessionStorage', createMemoryStorage());
}
